import { useState } from 'react';
import { Plus, Pencil, Trash2, Thermometer } from 'lucide-react';
import {
  useCycles,
  useDeleteCycle,
  useResources,
  useSaveCycle,
} from '../../api/referentiel';
import type { Cycle, CycleInput, LoadPlanItem, Resource, TcMode } from '../../api/types';
import { formatDuration, isTcDriven, TC_MODE_LABEL } from './format';
import { useCapabilities } from '../../api/capabilities';
import { fallbackCycleColor } from '../planning/colors';
import {
  ApiErrorMessage,
  Modal,
  NumberField,
  SelectField,
  TextField,
} from './ResourcesPanel';

function emptyCycle(): CycleInput {
  return {
    code: '',
    label: '',
    color: null,
    minDurationMinutes: 300,
    maxDurationMinutes: 300,
    plateauDurationMinutes: null,
    loadingMinutes: 30,
    unloadingMinutes: 20,
    temperatureC: 180,
    tcMode: 'SANS_TC',
    tcPerPart: null,
    allowedResourceIds: [],
    loadPlanItems: [],
  };
}

export default function CyclesPanel() {
  const { data: cycles, isLoading, error } = useCycles();
  const { data: resources } = useResources();
  const { tcFin } = useCapabilities();
  const save = useSaveCycle();
  const remove = useDeleteCycle();
  const [editing, setEditing] = useState<{ id?: number; data: CycleInput } | null>(null);

  if (isLoading) return <p className="text-gray-500">Chargement des cycles…</p>;
  if (error) return <p className="text-red-600">Erreur de chargement des cycles.</p>;

  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-lg font-semibold text-gray-800">Cycles de polymérisation</h2>
        <button
          className="bg-blue-600 hover:bg-blue-700 text-white px-3 py-2 rounded flex items-center gap-2"
          onClick={() => setEditing({ data: emptyCycle() })}
        >
          <Plus size={18} /> Nouveau cycle
        </button>
      </div>

      <div className="bg-white rounded-lg shadow overflow-x-auto">
        <table className="min-w-full text-sm">
          <thead className="bg-gray-50 text-left text-xs uppercase text-gray-500">
            <tr>
              <th className="px-4 py-3">Code</th>
              <th className="px-4 py-3">Libellé</th>
              <th className="px-4 py-3">Durée</th>
              <th className="px-4 py-3">Charg. / Déch.</th>
              {tcFin && <th className="px-4 py-3">Mode Tc</th>}
              <th className="px-4 py-3">Moyens</th>
              <th className="px-4 py-3"></th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {cycles?.map((c) => (
              <CycleRow
                key={c.id}
                cycle={c}
                resources={resources ?? []}
                showTcMode={tcFin}
                onEdit={() => setEditing({ id: c.id, data: toInput(c) })}
                onDelete={() => {
                  if (confirm(`Supprimer le cycle ${c.code} ?`)) remove.mutate(c.id);
                }}
              />
            ))}
          </tbody>
        </table>
      </div>

      {editing && (
        <CycleModal
          value={editing}
          resources={resources ?? []}
          tcFin={tcFin}
          saving={save.isPending}
          error={save.error}
          onClose={() => {
            save.reset();
            setEditing(null);
          }}
          onSubmit={(data) =>
            save.mutate(
              { id: editing.id, data },
              {
                onSuccess: () => {
                  save.reset();
                  setEditing(null);
                },
              },
            )
          }
        />
      )}
    </div>
  );
}

function toInput(c: Cycle): CycleInput {
  return {
    code: c.code,
    label: c.label,
    color: c.color,
    minDurationMinutes: c.minDurationMinutes,
    maxDurationMinutes: c.maxDurationMinutes,
    plateauDurationMinutes: c.plateauDurationMinutes,
    loadingMinutes: c.loadingMinutes,
    unloadingMinutes: c.unloadingMinutes,
    temperatureC: c.temperatureC,
    tcMode: c.tcMode,
    tcPerPart: c.tcPerPart,
    allowedResourceIds: c.allowedResources?.map((a) => a.resourceId) ?? [],
    loadPlanItems: c.loadPlanItems ?? [],
  };
}

function CycleRow({ cycle, resources, showTcMode, onEdit, onDelete }: { cycle: Cycle; resources: Resource[]; showTcMode: boolean; onEdit: () => void; onDelete: () => void }) {
  const driven = isTcDriven(cycle.minDurationMinutes, cycle.maxDurationMinutes);
  const allowed = cycle.allowedResources?.map((a) => a.resourceId) ?? [];

  return (
    <tr className="hover:bg-gray-50">
      <td className="px-4 py-3 font-medium text-gray-900">{cycle.code}</td>
      <td className="px-4 py-3 text-gray-600">{cycle.label}</td>
      <td className="px-4 py-3 text-gray-600">
        {driven ? (
          <span className="inline-flex items-center gap-1" title="Piloté par les Tc : durée non déterministe">
            <Thermometer size={14} className="text-amber-500" />
            {formatDuration(cycle.minDurationMinutes)} → {formatDuration(cycle.maxDurationMinutes)}
          </span>
        ) : (
          <span title="À l'ambiance : durée déterministe">{formatDuration(cycle.minDurationMinutes)}</span>
        )}
      </td>
      <td className="px-4 py-3 text-gray-600">
        {formatDuration(cycle.loadingMinutes)} / {formatDuration(cycle.unloadingMinutes)}
      </td>
      {showTcMode && (
        <td className="px-4 py-3">
          <span className="inline-block px-2 py-0.5 rounded-full text-xs bg-purple-100 text-purple-800">{TC_MODE_LABEL[cycle.tcMode]}</span>
        </td>
      )}
      <td className="px-4 py-3">
        <div className="flex flex-wrap gap-1">
          {allowed.map((id) => {
            const r = resources.find((res) => res.id === id);
            return r ? (
              <span key={id} className="px-2 py-0.5 rounded text-xs bg-blue-100 text-blue-800">
                {r.label}
              </span>
            ) : null;
          })}
        </div>
      </td>
      <td className="px-4 py-3">
        <div className="flex gap-2 justify-end">
          <button className="text-blue-600 hover:text-blue-800" onClick={onEdit} aria-label="Modifier">
            <Pencil size={18} />
          </button>
          <button className="text-red-600 hover:text-red-800" onClick={onDelete} aria-label="Supprimer">
            <Trash2 size={18} />
          </button>
        </div>
      </td>
    </tr>
  );
}

function CycleModal({
  value,
  resources,
  tcFin,
  saving,
  error,
  onClose,
  onSubmit,
}: {
  value: { id?: number; data: CycleInput };
  resources: Resource[];
  tcFin: boolean;
  saving: boolean;
  error: unknown;
  onClose: () => void;
  onSubmit: (data: CycleInput) => void;
}) {
  const [form, setForm] = useState<CycleInput>(value.data);
  const set = <K extends keyof CycleInput>(key: K, v: CycleInput[K]) => setForm((f) => ({ ...f, [key]: v }));

  const driven = isTcDriven(form.minDurationMinutes, form.maxDurationMinutes);

  function changeMode(mode: TcMode) {
    // On nettoie les champs devenus hors sujet pour ne pas envoyer d'incoherence
    // que l'API rejetterait (ex. plan sur un cycle SANS_TC).
    setForm((f) => ({
      ...f,
      tcMode: mode,
      tcPerPart: mode === 'TC_PAR_PIECE' ? f.tcPerPart : null,
      loadPlanItems: mode === 'PLAN_DE_CHARGEMENT' ? f.loadPlanItems : [],
    }));
  }

  function toggleResource(id: number) {
    setForm((f) => ({
      ...f,
      allowedResourceIds: f.allowedResourceIds.includes(id)
        ? f.allowedResourceIds.filter((x) => x !== id)
        : [...f.allowedResourceIds, id],
    }));
  }

  return (
    <Modal title={value.id ? 'Modifier le cycle' : 'Nouveau cycle'} onClose={onClose}>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          onSubmit(form);
        }}
        className="space-y-4"
      >
        <div className="grid grid-cols-2 gap-4">
          <TextField label="Code" value={form.code} onChange={(v) => set('code', v)} required />
          <TextField label="Libellé" value={form.label} onChange={(v) => set('label', v)} required />
          <NumberField label="Durée minimum (min)" value={form.minDurationMinutes} onChange={(v) => set('minDurationMinutes', v)} min={1} />
          <NumberField label="Durée maximum (min)" value={form.maxDurationMinutes} onChange={(v) => set('maxDurationMinutes', v)} min={1} />
        </div>

        <ColorField label="Couleur d'affichage" code={form.code} value={form.color} onChange={(v) => set('color', v)} />

        <p className="text-xs text-gray-500 -mt-1">
          {driven
            ? "Durées différentes : cycle piloté par les Tc. Le moyen sera réservé sur la durée maximum ; l'écart s'affichera comme marge d'incertitude sur le planning."
            : "Durées égales : cycle mené à l'ambiance, fin déterministe."}
        </p>

        <div className="grid grid-cols-3 gap-4">
          <NumberField label="Chargement (min)" value={form.loadingMinutes} onChange={(v) => set('loadingMinutes', v)} min={0} />
          <NumberField label="Déchargement (min)" value={form.unloadingMinutes} onChange={(v) => set('unloadingMinutes', v)} min={0} />
          <NumberField label="Température (°C)" value={form.temperatureC ?? 0} onChange={(v) => set('temperatureC', v)} />
        </div>

        {/* Section Tc : Option 2 (gestion fine des Tc). Masquée en MVP. */}
        {tcFin && (
          <>
            <div className="grid grid-cols-2 gap-4 items-start">
              <SelectField
                label="Mode Tc"
                value={form.tcMode}
                onChange={(v) => changeMode(v as TcMode)}
                options={[['SANS_TC', 'Sans Tc'], ['TC_PAR_PIECE', 'Tc par pièce'], ['PLAN_DE_CHARGEMENT', 'Plan de chargement']]}
              />
              {form.tcMode === 'TC_PAR_PIECE' && (
                <label className="block">
                  <span className="text-sm text-gray-600">Tc par pièce</span>
                  <input
                    type="number"
                    min={1}
                    placeholder="hérité du produit"
                    className="mt-1 w-full border rounded px-3 py-2"
                    value={form.tcPerPart ?? ''}
                    onChange={(e) => set('tcPerPart', e.target.value === '' ? null : Number(e.target.value))}
                  />
                  <span className="text-xs text-gray-400">Laisser vide pour utiliser le nombre défini au produit.</span>
                </label>
              )}
            </div>

            {form.tcMode === 'PLAN_DE_CHARGEMENT' && (
              <LoadPlanEditor items={form.loadPlanItems} onChange={(items) => set('loadPlanItems', items)} />
            )}
          </>
        )}

        <fieldset>
          <legend className="text-sm text-gray-600 mb-1">Moyens autorisés</legend>
          <div className="flex flex-wrap gap-2">
            {resources.map((r) => {
              const on = form.allowedResourceIds.includes(r.id);
              return (
                <button
                  type="button"
                  key={r.id}
                  onClick={() => toggleResource(r.id)}
                  className={`px-3 py-1.5 rounded border text-sm ${on ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-gray-700 border-gray-300'}`}
                >
                  {r.label}
                </button>
              );
            })}
          </div>
        </fieldset>

        <ApiErrorMessage error={error} />

        <div className="flex justify-end gap-2 pt-2">
          <button type="button" className="px-4 py-2 rounded border" onClick={onClose}>
            Annuler
          </button>
          <button type="submit" className="px-4 py-2 rounded bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50" disabled={saving}>
            {saving ? 'Enregistrement…' : 'Enregistrer'}
          </button>
        </div>
      </form>
    </Modal>
  );
}

/** Sélecteur de couleur avec bascule « auto » (couleur dérivée du code). */
function ColorField({ label, code, value, onChange }: { label: string; code: string; value: string | null; onChange: (v: string | null) => void }) {
  const effective = value ?? fallbackCycleColor(code || 'x');
  return (
    <div>
      <span className="text-sm text-gray-600">{label}</span>
      <div className="mt-1 flex items-center gap-3">
        <span className="w-6 h-6 rounded border" style={{ backgroundColor: effective }} title={effective} />
        <input
          type="color"
          value={effective}
          onChange={(e) => onChange(e.target.value)}
          className="h-9 w-14 border rounded cursor-pointer"
          aria-label="Choisir une couleur"
        />
        <label className="flex items-center gap-1 text-sm text-gray-600">
          <input type="checkbox" checked={value == null} onChange={(e) => onChange(e.target.checked ? null : effective)} />
          Auto (d'après le code)
        </label>
      </div>
    </div>
  );
}

function LoadPlanEditor({ items, onChange }: { items: LoadPlanItem[]; onChange: (items: LoadPlanItem[]) => void }) {
  function add() {
    const nextPosition = items.reduce((max, i) => Math.max(max, i.position), 0) + 1;
    onChange([...items, { position: nextPosition, productRef: '', hasTc: false, quantity: 1 }]);
  }
  function update(index: number, patch: Partial<LoadPlanItem>) {
    onChange(items.map((it, i) => (i === index ? { ...it, ...patch } : it)));
  }
  function remove(index: number) {
    onChange(items.filter((_, i) => i !== index));
  }

  const tcTotal = items.reduce((sum, i) => sum + (i.hasTc ? i.quantity : 0), 0);

  return (
    <div className="border rounded p-3 bg-gray-50">
      <div className="flex justify-between items-center mb-2">
        <span className="text-sm font-medium text-gray-700">Plan de chargement</span>
        <span className="text-xs text-gray-500">Tc du plan : <strong>{tcTotal}</strong></span>
      </div>
      <div className="space-y-2">
        {items.map((item, i) => (
          <div key={i} className="grid grid-cols-[3rem_1fr_5rem_auto_2rem] gap-2 items-center">
            <input type="number" min={1} className="border rounded px-2 py-1 text-sm" value={item.position} onChange={(e) => update(i, { position: Number(e.target.value) })} aria-label="Position" />
            <input className="border rounded px-2 py-1 text-sm" placeholder="Réf. produit" value={item.productRef} onChange={(e) => update(i, { productRef: e.target.value })} />
            <input type="number" min={1} className="border rounded px-2 py-1 text-sm" value={item.quantity} onChange={(e) => update(i, { quantity: Number(e.target.value) })} aria-label="Quantité" />
            <label className="flex items-center gap-1 text-sm">
              <input type="checkbox" checked={item.hasTc} onChange={(e) => update(i, { hasTc: e.target.checked })} /> Tc
            </label>
            <button type="button" className="text-red-500" onClick={() => remove(i)} aria-label="Retirer">
              <Trash2 size={16} />
            </button>
          </div>
        ))}
      </div>
      <button type="button" className="mt-2 text-sm text-blue-600 flex items-center gap-1" onClick={add}>
        <Plus size={14} /> Ajouter une position
      </button>
    </div>
  );
}
